import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import { useFamily } from '@/contexts/FamilyContext'
import { getTodayDateString, extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import { ChevronLeft, ChevronRight, Calendar, X, Eye, TrendingUp, Users, CheckCircle2, Star, Zap, Target, Award } from 'lucide-react'
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, addYears, subYears, startOfYear } from 'date-fns'

interface LeaderboardEntry {
  id: string
  display_name: string
  color_theme: string
  points: number
  total_points: number
  weekly_points: number
  level: number
  streak_days: number
  rank: number
}

interface BreakdownData {
  user: string
  period: string
  breakdown: {
    chores?: Array<{ date: string; description: string; points: number }>
    bonuses?: Array<{ date: string; description: string; points: number }>
    deductions?: Array<{ date: string; description: string; points: number }>
  }
  totals: {
    earned: number
    bonuses: number
    deductions: number
    adjustments: number
    leaderboard_total: number
    spent: number
    available_balance: number
  }
}

interface PointTransaction {
  id: string
  user_id?: string
  points: number
  transaction_type: string
  description: string
  created_at: string
  user_name?: string
  user_color?: string
}

/**
 * Analytics page - Shows family statistics and performance metrics
 * Route: /dashboard/analytics
 */
export const AnalyticsPage: React.FC = () => {
  const { getChildren } = useFamily()
  const children = getChildren()
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'leaderboard'>('overview')
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [pointsHistory, setPointsHistory] = useState<PointTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<'day' | 'week' | 'month' | 'year' | 'all'>('month')
  const [selectedWeek, setSelectedWeek] = useState(new Date())
  const [selectedMonth, setSelectedMonth] = useState(new Date())
  const [selectedYear, setSelectedYear] = useState(new Date())
  const [showBreakdownModal, setShowBreakdownModal] = useState(false)
  const [childBreakdown, setChildBreakdown] = useState<BreakdownData | null>(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [completedToday, setCompletedToday] = useState(0)
  const [weeklyCompletionRate, setWeeklyCompletionRate] = useState(0)

  useEffect(() => {
    loadData()
  }, [leaderboardPeriod, selectedWeek, selectedMonth, selectedYear])

  const loadData = async () => {
    setLoading(true)
    try {
      // Calculate offset for period navigation
      let offset = 0
      if (leaderboardPeriod === 'week') {
        const now = new Date()
        const currentWeekStart = startOfWeek(now, { weekStartsOn: 0 })
        const selectedWeekStart = startOfWeek(selectedWeek, { weekStartsOn: 0 })
        const weekDiff = Math.floor((currentWeekStart.getTime() - selectedWeekStart.getTime()) / (1000 * 60 * 60 * 24 * 7))
        offset = -weekDiff
      } else if (leaderboardPeriod === 'month') {
        const now = new Date()
        const currentMonthStart = startOfMonth(now)
        const selectedMonthStart = startOfMonth(selectedMonth)
        const yearDiff = currentMonthStart.getFullYear() - selectedMonthStart.getFullYear()
        const monthDiff = currentMonthStart.getMonth() - selectedMonthStart.getMonth()
        offset = -(yearDiff * 12 + monthDiff)
      } else if (leaderboardPeriod === 'year') {
        const now = new Date()
        const currentYearStart = startOfYear(now)
        const selectedYearStart = startOfYear(selectedYear)
        const yearDiff = currentYearStart.getFullYear() - selectedYearStart.getFullYear()
        offset = -yearDiff
      }

      // Load leaderboard with selected period
      const leaderboardData = await apiClient.getLeaderboard(leaderboardPeriod, offset)
      setLeaderboard(Array.isArray(leaderboardData) ? leaderboardData : [])

      // Get completed today count and weekly completion rate
      const todayStr = await getTodayDateString()
      const assignmentsRes = await apiClient.get('/api/assignments')
      const assignments = assignmentsRes.assignments || assignmentsRes.data || []
      const todayCompleted = assignments.filter((a: any) =>
        (a.status === 'completed' || a.status === 'verified') && a.completed_at && a.completed_at.startsWith(todayStr)
      ).length
      setCompletedToday(todayCompleted)

      // Calculate weekly completion rate
      // Calculate week-ago date from family timezone "today"
      const [year, month, day] = todayStr.split('-').map(Number)
      const weekAgoDate = new Date(year, month - 1, day - 7)
      const weekAgoStr = `${weekAgoDate.getFullYear()}-${String(weekAgoDate.getMonth() + 1).padStart(2, '0')}-${String(weekAgoDate.getDate()).padStart(2, '0')}`
      const weeklyAssignments = assignments.filter((a: any) => {
        const dueDateStr = a.due_date ? extractDateInTimezoneSync(a.due_date) : null
        return dueDateStr && dueDateStr >= weekAgoStr && dueDateStr <= todayStr
      })
      const weeklyCompleted = weeklyAssignments.filter((a: any) => a.status === 'completed' || a.status === 'verified').length
      const rate = weeklyAssignments.length > 0
        ? Math.round((weeklyCompleted / weeklyAssignments.length) * 100)
        : 0
      setWeeklyCompletionRate(rate)

      // Load points history for all kids
      if (children.length > 0) {
        const allHistories = await Promise.all(
          children.map(async (child) => {
            try {
              const history = await apiClient.getPointsHistory(child.id, 10)
              return (history.transactions || []).map((t: PointTransaction) => ({
                ...t,
                user_id: child.id,
                user_name: child.display_name,
                user_color: child.color_theme
              }))
            } catch {
              return []
            }
          })
        )
        const combined = allHistories
          .flat()
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 30)
        setPointsHistory(combined)
      }
    } catch (error) {
      console.error('Failed to load analytics:', error)
    }
    setLoading(false)
  }

  const fetchChildBreakdown = async (username: string) => {
    setBreakdownLoading(true)
    try {
      const periodMap: Record<string, string> = {
        'day': 'day',
        'week': 'week',
        'month': 'month',
        'year': 'year',
        'all': 'all-time'
      }

      // Calculate offset
      let offset = 0
      const now = new Date()
      if (leaderboardPeriod === 'week') {
        const currentWeekStart = startOfWeek(now, { weekStartsOn: 0 })
        const selectedWeekStart = startOfWeek(selectedWeek, { weekStartsOn: 0 })
        offset = Math.floor((currentWeekStart.getTime() - selectedWeekStart.getTime()) / (1000 * 60 * 60 * 24 * 7))
      } else if (leaderboardPeriod === 'month') {
        const currentMonthStart = startOfMonth(now)
        const selectedMonthStart = startOfMonth(selectedMonth)
        const yearDiff = currentMonthStart.getFullYear() - selectedMonthStart.getFullYear()
        const monthDiff = currentMonthStart.getMonth() - selectedMonthStart.getMonth()
        offset = yearDiff * 12 + monthDiff
      } else if (leaderboardPeriod === 'year') {
        const currentYearStart = startOfYear(now)
        const selectedYearStart = startOfYear(selectedYear)
        offset = currentYearStart.getFullYear() - selectedYearStart.getFullYear()
      }

      const offsetParam = offset !== 0 ? `&offset=${offset}` : ''
      const response = await apiClient.get(`/api/points/breakdown/${username}?period=${periodMap[leaderboardPeriod]}${offsetParam}`)
      setChildBreakdown(response)
      setShowBreakdownModal(true)
    } catch (error) {
      console.error('Error fetching breakdown:', error)
    } finally {
      setBreakdownLoading(false)
    }
  }

  // Calculate totals from leaderboard
  const totalFamilyPoints = leaderboard.reduce((sum, entry) => sum + (entry.points || 0), 0)
  const totalAvailablePoints = leaderboard.reduce((sum, entry) => sum + ((entry as any).available_points ?? entry.total_points ?? 0), 0)
  const totalLifetimePoints = leaderboard.reduce((sum, entry) => sum + ((entry as any).lifetime_points_earned ?? entry.total_points ?? 0), 0)

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'chore_completion': return '✅'
      case 'weekly_bonus': return '🎯'
      case 'streak_bonus': return '🔥'
      case 'purchase': return '🛒'
      case 'trade': return '🔄'
      default: return '💰'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    )
  }

  const getPeriodLabel = () => {
    switch (leaderboardPeriod) {
      case 'day': return 'Today'
      case 'week': return 'This Week'
      case 'month': return 'This Month'
      case 'year': return 'This Year'
      case 'all': return 'All Time'
    }
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'overview'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'history'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          History
        </button>
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'leaderboard'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Leaderboard
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Main Stats - Colorful Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Available Points</div>
              <div className="text-3xl font-bold">{totalAvailablePoints.toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">family total</div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">This Month</div>
              <div className="text-3xl font-bold">{totalFamilyPoints.toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">earned this month</div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Weekly Rate</div>
              <div className="text-3xl font-bold">{weeklyCompletionRate}%</div>
              <div className="text-xs opacity-75 mt-1">completion rate</div>
            </div>

            <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Completed</div>
              <div className="text-3xl font-bold">{completedToday}</div>
              <div className="text-xs opacity-75 mt-1">chores today</div>
            </div>
          </div>

          {/* Kids Overview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Family Members
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {leaderboard.map((kid) => (
                <div key={kid.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: kid.color_theme || '#6366f1' }}
                    >
                      {kid.display_name[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{kid.display_name}</div>
                      <div className="text-sm text-gray-500">Level {kid.level} • {kid.streak_days} day streak</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{ color: kid.color_theme || '#6366f1' }}>
                      {(kid as any).available_points ?? kid.total_points}
                    </div>
                    <div className="text-xs text-gray-500">available</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-green-600" />
              Quick Stats
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Kids</span>
                <span className="font-bold text-purple-600 text-lg">{leaderboard.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Weekly</span>
                <span className="font-bold text-green-600 text-lg">{weeklyCompletionRate}%</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Today</span>
                <span className="font-bold text-blue-600 text-lg">{completedToday}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Month Total</span>
                <span className="font-bold text-amber-600 text-lg">{totalFamilyPoints}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Recent Points Activity</h2>
          <p className="text-sm text-gray-600 mb-3">Last 30 transactions across all kids</p>

          {pointsHistory.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 text-center py-8">
              <div className="text-4xl mb-2">📊</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No points history yet!</h3>
              <p className="text-gray-600">Points will appear here as kids complete chores.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="divide-y divide-gray-100">
                {pointsHistory.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {getTransactionIcon(transaction.transaction_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                          {transaction.description}
                          {transaction.user_name && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: transaction.user_color || '#6B7280' }}
                            >
                              {transaction.user_name}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                          <span>{formatDate(transaction.created_at)}</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-400 text-xs">
                            {new Date(transaction.created_at).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`font-bold text-lg whitespace-nowrap ${
                      transaction.points > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.points > 0 ? '+' : ''}{transaction.points}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <>
          {/* Period Selector */}
          <div className="flex flex-wrap gap-2">
            {(['day', 'week', 'month', 'year', 'all'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setLeaderboardPeriod(period)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  leaderboardPeriod === period
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {period === 'day' ? 'Today' : period === 'week' ? 'Week' : period === 'month' ? 'Month' : period === 'year' ? 'Year' : 'All Time'}
              </button>
            ))}
          </div>

          {/* Period Navigator */}
      {leaderboardPeriod === 'week' && (
        <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
          <button
            onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {format(startOfWeek(selectedWeek, { weekStartsOn: 0 }), 'MMM d')} -
            {format(endOfWeek(selectedWeek, { weekStartsOn: 0 }), 'MMM d, yyyy')}
          </div>
          <button
            onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            disabled={selectedWeek >= new Date()}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {leaderboardPeriod === 'month' && (
        <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
          <button
            onClick={() => setSelectedMonth(subMonths(selectedMonth, 1))}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {format(selectedMonth, 'MMMM yyyy')}
          </div>
          <button
            onClick={() => setSelectedMonth(addMonths(selectedMonth, 1))}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            disabled={startOfMonth(selectedMonth) >= startOfMonth(new Date())}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {leaderboardPeriod === 'year' && (
        <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
          <button
            onClick={() => setSelectedYear(subYears(selectedYear, 1))}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {format(selectedYear, 'yyyy')}
          </div>
          <button
            onClick={() => setSelectedYear(addYears(selectedYear, 1))}
            className="p-2 rounded hover:bg-gray-100 transition-colors"
            disabled={startOfYear(selectedYear) >= startOfYear(new Date())}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

          {/* Summary Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Family Total</div>
              <div className="text-3xl font-bold">{totalFamilyPoints.toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">{getPeriodLabel().toLowerCase()}</div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Available</div>
              <div className="text-3xl font-bold">{totalAvailablePoints.toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">to spend</div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Kids</div>
              <div className="text-3xl font-bold">{leaderboard.length}</div>
              <div className="text-xs opacity-75 mt-1">family members</div>
            </div>

            <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Completed</div>
              <div className="text-3xl font-bold">{completedToday}</div>
              <div className="text-xs opacity-75 mt-1">chores today</div>
            </div>
          </div>

          {/* Family Leaderboard */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Family Leaderboard
          <span className="text-sm font-normal text-gray-500 ml-2">
            {getPeriodLabel()}
          </span>
        </h2>

        {leaderboard.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">📊</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No data yet</h3>
            <p className="text-gray-600">Points will appear here as kids complete chores.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((entry) => {
              const isTop3 = entry.rank <= 3
              const medalColors: Record<number, string> = {
                1: 'from-yellow-400 to-amber-500',
                2: 'from-gray-300 to-gray-400',
                3: 'from-orange-400 to-amber-600'
              }
              const medalEmoji: Record<number, string> = {
                1: '🥇',
                2: '🥈',
                3: '🥉'
              }

              return (
                <div
                  key={entry.id}
                  onClick={() => {
                    const username = entry.display_name.toLowerCase().replace(' ', '')
                    fetchChildBreakdown(username)
                  }}
                  className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                    isTop3
                      ? `bg-gradient-to-r ${medalColors[entry.rank]} text-white shadow-md`
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    {isTop3 ? (
                      <div className="text-3xl">{medalEmoji[entry.rank]}</div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600">
                        #{entry.rank}
                      </div>
                    )}
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm ${
                        isTop3 ? 'ring-2 ring-white/50' : ''
                      }`}
                      style={{ backgroundColor: entry.color_theme || '#6366f1' }}
                    >
                      {entry.display_name[0]}
                    </div>
                    <div>
                      <div className={`font-semibold flex items-center gap-2 ${isTop3 ? 'text-white' : 'text-gray-900'}`}>
                        {entry.display_name}
                        <Eye className={`w-4 h-4 ${isTop3 ? 'text-white/70' : 'text-gray-400'}`} />
                      </div>
                      <div className={`text-sm ${isTop3 ? 'text-white/80' : 'text-gray-500'}`}>
                        Level {entry.level} • {entry.streak_days} day streak
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${isTop3 ? 'text-white' : ''}`} style={isTop3 ? {} : { color: entry.color_theme || '#6366f1' }}>
                      {entry.points?.toLocaleString()}
                    </div>
                    <div className={`text-sm ${isTop3 ? 'text-white/80' : 'text-gray-500'}`}>
                      {getPeriodLabel().toLowerCase()}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
          </div>
        </>
      )}

      {/* Points Breakdown Modal */}
      {showBreakdownModal && childBreakdown && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{childBreakdown.user}</h2>
                  <p className="text-sm opacity-90">Points Statement</p>
                  <p className="text-xs opacity-75 mt-1">{childBreakdown.period}</p>
                </div>
                <button
                  onClick={() => {
                    setShowBreakdownModal(false)
                    setChildBreakdown(null)
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Chores Section */}
              {childBreakdown.breakdown.chores && childBreakdown.breakdown.chores.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">CHORES COMPLETED</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.chores.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description.replace('Completed: ', '')}</span>
                        </div>
                        <span className="text-green-600 font-mono">+{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-green-600 font-mono font-bold">+{childBreakdown.totals.earned}</span>
                  </div>
                </div>
              )}

              {/* Bonuses Section */}
              {childBreakdown.breakdown.bonuses && childBreakdown.breakdown.bonuses.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">BONUSES</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.bonuses.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description}</span>
                        </div>
                        <span className="text-blue-600 font-mono">+{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-blue-600 font-mono font-bold">+{childBreakdown.totals.bonuses}</span>
                  </div>
                </div>
              )}

              {/* Deductions Section */}
              {childBreakdown.breakdown.deductions && childBreakdown.breakdown.deductions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">DEDUCTIONS</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.deductions.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description}</span>
                        </div>
                        <span className="text-red-600 font-mono">{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-red-600 font-mono font-bold">{childBreakdown.totals.deductions}</span>
                  </div>
                </div>
              )}

              {/* Final Calculation */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xs font-semibold text-gray-500 mb-4">CALCULATION</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Earned from Chores</span>
                    <span className="font-mono">+{childBreakdown.totals.earned}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Bonuses</span>
                    <span className="font-mono">+{childBreakdown.totals.bonuses}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Deductions</span>
                    <span className="font-mono">{childBreakdown.totals.deductions}</span>
                  </div>
                  {childBreakdown.totals.adjustments !== 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Adjustments</span>
                      <span className="font-mono">{childBreakdown.totals.adjustments > 0 ? '+' : ''}{childBreakdown.totals.adjustments}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 mt-2 border-t text-gray-900 font-semibold">
                    <span>PERIOD TOTAL</span>
                    <span className="font-mono text-blue-600 text-lg">{childBreakdown.totals.leaderboard_total}</span>
                  </div>
                </div>

                {childBreakdown.totals.spent > 0 && (
                  <div className="mt-4 pt-4 border-t space-y-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Rewards Redeemed</span>
                      <span className="font-mono">-{childBreakdown.totals.spent}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 mt-2 border-t text-gray-900 font-semibold">
                      <span>AVAILABLE BALANCE</span>
                      <span className="font-mono text-green-600 text-lg">{childBreakdown.totals.available_balance}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
